import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Zap, Users, Building2, Loader2, CreditCard } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PricingPage = () => {
  const { user, isAuthenticated } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const handleCheckout = async (packageId) => {
    if (!isAuthenticated) {
      // Save intended package and redirect to login
      localStorage.setItem('intended_package', packageId);
      navigate('/register');
      return;
    }

    setLoading(packageId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/checkout/create`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setLoading(null);
    }
  };

  const plans = [
    {
      id: null,
      name: t('pricing_free'),
      price: t('pricing_free_price'),
      period: '',
      icon: Zap,
      features: [
        t('pricing_free_feature_1'),
        t('pricing_free_feature_2'),
        t('pricing_free_feature_3'),
      ],
      cta: isAuthenticated ? null : t('pricing_cta'),
      href: '/register',
      current: user?.plan === 'free',
      highlight: false,
    },
    {
      id: 'pro_monthly',
      name: t('pricing_pro'),
      price: t('pricing_pro_price'),
      period: t('pricing_pro_month'),
      icon: Users,
      features: [
        t('pricing_pro_feature_1'),
        t('pricing_pro_feature_2'),
        t('pricing_pro_feature_3'),
        t('pricing_pro_feature_4'),
        t('pricing_pro_feature_5'),
      ],
      cta: language === 'nl' ? 'Upgrade naar Pro' : 'Upgrade to Pro',
      current: user?.plan === 'pro',
      highlight: true,
      badge: 'Popular',
    },
    {
      id: 'team_monthly',
      name: t('pricing_team'),
      price: t('pricing_team_price'),
      period: t('pricing_team_month'),
      icon: Building2,
      features: [
        t('pricing_team_feature_1'),
        t('pricing_team_feature_2'),
        t('pricing_team_feature_3'),
        t('pricing_team_feature_4'),
      ],
      cta: language === 'nl' ? 'Upgrade naar Team' : 'Upgrade to Team',
      current: user?.plan === 'team',
      highlight: false,
    },
  ];

  return (
    <div className="py-12 md:py-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-4">
            {t('pricing_title')}
          </h1>
          <p className="text-lg text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
            {t('pricing_subtitle')}
          </p>
        </motion.div>

        {/* Pricing cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`
                relative p-8 rounded-2xl border-2 transition-all
                ${plan.highlight 
                  ? 'border-accent bg-white dark:bg-slate-900 shadow-xl shadow-accent/10' 
                  : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'}
                ${plan.current ? 'ring-2 ring-accent ring-offset-2' : ''}
              `}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-1 rounded-full text-sm font-semibold bg-accent text-white">
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Current plan indicator */}
              {plan.current && (
                <div className="absolute -top-3 right-4">
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-success text-white">
                    {t('pricing_current')}
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className={`
                w-12 h-12 rounded-xl flex items-center justify-center mb-6
                ${plan.highlight ? 'bg-accent/10' : 'bg-slate-100 dark:bg-slate-800'}
              `}>
                <plan.icon className={`w-6 h-6 ${plan.highlight ? 'text-accent' : 'text-slate-500'}`} />
              </div>

              {/* Name */}
              <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white mb-2">
                {plan.name}
              </h3>

              {/* Price */}
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-heading font-bold text-4xl text-slate-900 dark:text-white">
                  {plan.price}
                </span>
                {plan.period && (
                  <span className="text-slate-500 dark:text-slate-400">
                    {plan.period}
                  </span>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 shrink-0 ${plan.highlight ? 'text-accent' : 'text-success'}`} />
                    <span className="text-slate-600 dark:text-slate-300 text-sm">
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              {plan.cta && !plan.current && (
                plan.id ? (
                  <button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={loading === plan.id}
                    className={`
                      w-full py-3 px-6 rounded-xl font-medium text-center transition-colors btn-press flex items-center justify-center gap-2
                      ${plan.highlight 
                        ? 'bg-accent text-white hover:bg-accent-hover disabled:opacity-50' 
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700'}
                    `}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {loading === plan.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5" />
                        {plan.cta}
                      </>
                    )}
                  </button>
                ) : (
                  <Link
                    to={plan.href}
                    className={`
                      block w-full py-3 px-6 rounded-xl font-medium text-center transition-colors btn-press
                      ${plan.highlight 
                        ? 'bg-accent text-white hover:bg-accent-hover' 
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 dark:hover:bg-slate-700'}
                    `}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </Link>
                )
              )}
            </motion.div>
          ))}
        </div>

        {/* Payment methods */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="inline-flex items-center gap-4 px-6 py-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl mb-4">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {language === 'nl' ? 'Betaalmethodes:' : 'Payment methods:'}
            </span>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-white dark:bg-slate-800 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700">
                Visa
              </span>
              <span className="px-3 py-1 bg-white dark:bg-slate-800 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700">
                Mastercard
              </span>
              <span className="px-3 py-1 bg-white dark:bg-slate-800 rounded-lg text-xs font-medium border border-slate-200 dark:border-slate-700">
                American Express
              </span>
            </div>
          </div>
          <p className="text-slate-500 dark:text-slate-400 max-w-2xl mx-auto text-sm">
            {language === 'nl' 
              ? 'Alle prijzen zijn exclusief BTW. Maandelijks opzegbaar. Veilige betaling via Stripe.'
              : 'All prices exclude VAT. Cancel monthly. Secure payment via Stripe.'}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
